import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-storage');
  if (token) {
    const { state } = JSON.parse(token);
    if (state.token) {
      config.headers.Authorization = `Bearer ${state.token}`;
    }
  }
  return config;
});

// Handle token expiration — auto-logout on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Don't auto-logout for login/register/password-reset requests
      const url = error.config?.url || '';
      const publicPaths = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
      const isPublicRequest = publicPaths.some(path => url.includes(path));

      if (!isPublicRequest) {
        // Clear auth state and redirect to login
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          const { state } = JSON.parse(authStorage);
          if (state.token) {
            // Clear the stored auth state
            localStorage.setItem('auth-storage', JSON.stringify({
              state: { user: null, token: null },
              version: 0
            }));
            // Redirect to login with expiration message
            window.location.href = '/login?expired=true';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get('/auth/reset-password/validate', { params: { token } }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  facebookLogin: () => {
    window.location.href = `${API_URL}/auth/facebook`;
  },
  instagramLogin: () => {
    window.location.href = `${API_URL}/auth/instagram`;
  },
  connectFacebook: () => {
    // Pass token as query parameter since browser redirect can't include Authorization header
    const tokenData = localStorage.getItem('auth-storage');
    if (tokenData) {
      const { state } = JSON.parse(tokenData);
      if (state.token) {
        window.location.href = `${API_URL}/auth/facebook/connect?token=${encodeURIComponent(state.token)}`;
        return;
      }
    }
    // If no token, redirect to login
    window.location.href = '/login';
  },
  disconnectFacebook: () => api.post('/auth/facebook/disconnect'),
  verifyFacebook: () => api.get('/auth/facebook/verify'),
  // YouTube OAuth
  youtubeLogin: () => {
    window.location.href = `${API_URL}/auth/youtube`;
  },
  connectYouTube: () => {
    // Pass token as query parameter since browser redirect can't include Authorization header
    const tokenData = localStorage.getItem('auth-storage');
    if (tokenData) {
      const { state } = JSON.parse(tokenData);
      if (state.token) {
        window.location.href = `${API_URL}/auth/youtube/connect?token=${encodeURIComponent(state.token)}`;
        return;
      }
    }
    // If no token, redirect to login
    window.location.href = '/login';
  },
  disconnectYouTube: () => api.post('/auth/youtube/disconnect'),
  verifyYouTube: () => api.get('/auth/youtube/verify'),
  // Instagram OAuth (native Instagram Login)
  connectInstagram: () => {
    const tokenData = localStorage.getItem('auth-storage');
    if (tokenData) {
      const { state } = JSON.parse(tokenData);
      if (state.token) {
        window.location.href = `${API_URL}/auth/instagram/connect?token=${encodeURIComponent(state.token)}`;
        return;
      }
    }
    window.location.href = '/login';
  },
  disconnectInstagram: () => api.post('/auth/instagram/disconnect'),
  verifyInstagram: () => api.get('/auth/instagram/verify'),
  getSocialAccounts: () => api.get('/auth/social-accounts'),
};

// Users
export const userAPI = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/me', data),
  getStats: () => api.get('/users/me/stats'),
  getTasksDue: (params) => api.get('/users/me/tasks-due', { params }),
  getActivity: (params) => api.get('/users/me/activity', { params }),
  getCharts: (params) => api.get('/users/me/charts', { params }),
  searchUsers: (query) => api.get('/users/search', { params: { q: query } }),
  // Admin
  getAll: (params) => api.get('/users', { params }),
  adminUpdate: (id, data) => api.put(`/users/${id}`, data),
};

// Tasks
export const taskAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getMyTasks: () => api.get('/tasks/my-tasks'),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  complete: (id, data) => api.post(`/tasks/${id}/complete`, data),
  getViewers: (id) => api.get(`/tasks/${id}/viewers`),
};

// Posts (text updates/announcements)
export const postAPI = {
  getAll: (params) => api.get('/posts', { params }),
  getById: (id) => api.get(`/posts/${id}`),
  create: (data) => api.post('/posts', data),
  update: (id, data) => api.put(`/posts/${id}`, data),
  delete: (id) => api.delete(`/posts/${id}`),
  like: (id) => api.post(`/posts/${id}/like`),
  addComment: (id, data) => api.post(`/posts/${id}/comments`, data),
  deleteComment: (id, commentId) => api.delete(`/posts/${id}/comments/${commentId}`),
};

// Messaging
export const messageAPI = {
  broadcast: (data) => api.post('/messages/broadcast', data),
  getConversations: () => api.get('/messages/conversations'),
  getOrCreateDirect: (userId) => api.post('/messages/conversations/direct', { user_id: userId }),
  createGroup: (data) => api.post('/messages/conversations/group', data),
  getMessages: (conversationId, params) => api.get(`/messages/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data) => api.post(`/messages/conversations/${conversationId}/messages`, data),
  deleteMessage: (conversationId, messageId) => api.delete(`/messages/conversations/${conversationId}/messages/${messageId}`),
  addParticipant: (conversationId, userId) => api.post(`/messages/conversations/${conversationId}/participants`, { user_id: userId }),
  leaveConversation: (conversationId) => api.delete(`/messages/conversations/${conversationId}/leave`),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Campaigns
export const campaignAPI = {
  getAll: () => api.get('/campaigns'),
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  getContent: (id) => api.get(`/campaigns/${id}/content`),
  getLibrary: (id) => api.get(`/campaigns/${id}/library`),
};

// Content & Tracking
export const contentAPI = {
  getLibrary: () => api.get('/content/library'),
  submitContent: (data) => api.post('/content/submit', data),
  getSubmissions: () => api.get('/content/submissions'),
  trackPost: (data) => api.post('/content/track-post', data), // Manual post URL submission
  verifySubmission: (id, data) => api.patch(`/content/submissions/${id}/verify`, data),
  getPendingSubmissions: () => api.get('/content/submissions/pending'),
};

// Rewards
export const rewardAPI = {
  getAll: () => api.get('/rewards'),
  redeem: (id, data) => api.post(`/rewards/${id}/redeem`, data),
  getRedemptions: (params) => api.get('/rewards/redemptions', { params }),
  // Admin
  adminGetAll: () => api.get('/rewards/admin/all'),
  create: (data) => api.post('/rewards', data),
  update: (id, data) => api.put(`/rewards/${id}`, data),
  delete: (id) => api.delete(`/rewards/${id}`),
  adminGetRedemptions: (params) => api.get('/rewards/admin/redemptions', { params }),
  adminUpdateRedemption: (id, data) => api.patch(`/rewards/admin/redemptions/${id}`, data),
};

// Commissions
export const commissionAPI = {
  getSales: (params) => api.get('/commissions/sales', { params }),
  getEarnings: () => api.get('/commissions/earnings'),
  getPayouts: (params) => api.get('/commissions/payouts', { params }),
  // Admin
  adminGetSales: (params) => api.get('/commissions/admin/sales', { params }),
  adminUpdateSale: (id, data) => api.patch(`/commissions/admin/sales/${id}`, data),
};

// Analytics
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getLeaderboard: (params) => api.get('/analytics/leaderboard', { params }),
  getActivity: (limit) => api.get('/analytics/activity', { params: { limit } }),
  exportUsers: () => `${API_URL}/analytics/export/users`,
  exportSales: () => `${API_URL}/analytics/export/sales`,
  exportRedemptions: () => `${API_URL}/analytics/export/redemptions`,
  // Settings
  getSettings: () => api.get('/analytics/settings'),
  updateSettings: (settings) => api.put('/analytics/settings', { settings }),
  // Charts
  getSignupChart: () => api.get('/analytics/charts/signups'),
  getPointsChart: () => api.get('/analytics/charts/points'),
  getTopPerformers: () => api.get('/analytics/charts/top-performers'),
  getTierChart: () => api.get('/analytics/charts/tiers'),
};

// Notifications
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  send: (data) => api.post('/notifications/send', data),
};

// Referral Links
export const referralAPI = {
  getAll: (params) => api.get('/referrals', { params }),
  getById: (id) => api.get(`/referrals/${id}`),
  create: (data) => api.post('/referrals', data),
  update: (id, data) => api.put(`/referrals/${id}`, data),
  delete: (id) => api.delete(`/referrals/${id}`),
  getClicks: (id, params) => api.get(`/referrals/${id}/clicks`, { params }),
  // Admin
  adminGetAll: (params) => api.get('/referrals/admin/all', { params }),
  adminGetStats: () => api.get('/referrals/admin/stats'),
  adminSetDefaultDestination: (destination_url) => api.put('/referrals/admin/default-destination', { destination_url }),
};

// Social Media
export const socialAPI = {
  postToFacebook: (data) => api.post('/social/facebook/post', data),
  shareOnFacebook: (data) => api.post('/social/facebook/share', data),
  getFacebookProfile: () => api.get('/social/facebook/profile'),
  getFacebookProfilePicture: () => api.get('/social/facebook/profile-picture', { responseType: 'blob' }),
  getFacebookPages: () => api.get('/social/facebook/pages'),
  shareCampaignOnFacebook: (campaignId, data) => api.post(`/social/facebook/share-campaign/${campaignId}`, data),
  // YouTube
  getYouTubeChannel: () => api.get('/social/youtube/channel'),
  getYouTubeStats: () => api.get('/social/youtube/stats'),
  getYouTubeVideos: (maxResults = 10) => api.get('/social/youtube/videos', { params: { maxResults } }),
  getYouTubeVideoStats: (videoId) => api.get(`/social/youtube/videos/${videoId}/stats`),
  getYouTubePlaylists: (maxResults = 10) => api.get('/social/youtube/playlists', { params: { maxResults } }),
  // Instagram
  getInstagramProfile: () => api.get('/social/instagram/profile'),
  getInstagramMedia: (limit = 12) => api.get('/social/instagram/media', { params: { limit } }),
  getInstagramMediaInsights: (mediaId) => api.get(`/social/instagram/media/${mediaId}/insights`),
  getInstagramInsights: () => api.get('/social/instagram/insights'),
  publishToInstagram: (data) => api.post('/social/instagram/publish', data),
  publishCarouselToInstagram: (data) => api.post('/social/instagram/publish/carousel', data),
};

// Media Storage
export const storageAPI = {
  uploadFile: (file, onUploadProgress, prefix = '') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/storage/upload?prefix=${encodeURIComponent(prefix)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },
  getFiles: (params) => api.get('/storage', { params }),
  deleteFile: (key) => api.delete(`/storage/${key}`),
  createFolder: (folderPath) => api.post('/storage/folder', { folderPath }),
  deleteFolder: (prefix) => api.delete('/storage/folder', { data: { prefix } }),
};

export default api;
